import React from 'react';
import PropTypes from 'prop-types';

import {LineChart} from '@mui/x-charts';

import {Alert, Box, Chip, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import CohortGraphSkeleton from './skeletons/CohortGraphSkeleton';

import moment from 'moment-timezone';

const CohortGraphData = (props) => {
  const {
    chartHeight = 260,
    showSummary = true,
    titleExpression,
    stageKeys = [],
    isLoading,
    errorMessage,
    metricsByDate,
    currentDate,
    previousDates,
  } = props;

  const stageList = React.useMemo(() => ([
    {
      key: 'LEAD',
      label: translate('metrics.LeadFunnel.Cohort.Stages.LEAD'),
      dotColor: translate('client.Status.LEAD.MetricsColor.DotColor'),
      lineColor: translate('client.Status.LEAD.MetricsColor.LineColor'),
    },
    {
      key: 'CONTACT_MADE',
      label: translate('metrics.LeadFunnel.Cohort.Stages.CONTACT_MADE'),
      dotColor: translate('client.Status.CONTACT_MADE.MetricsColor.DotColor'),
      lineColor: translate('client.Status.CONTACT_MADE.MetricsColor.LineColor'),
    },
    {
      key: 'DETAILS_NEEDED',
      label: translate('metrics.LeadFunnel.Cohort.Stages.DETAILS_NEEDED'),
      dotColor: translate('client.Status.DETAILS_NEEDED.MetricsColor.DotColor'),
      lineColor: translate('client.Status.DETAILS_NEEDED.MetricsColor.LineColor'),
    },
    {
      key: 'CG_SEARCH',
      label: translate('metrics.LeadFunnel.Cohort.Stages.CG_SEARCH'),
      dotColor: translate('client.Status.CG_SEARCH.MetricsColor.DotColor'),
      lineColor: translate('client.Status.CG_SEARCH.MetricsColor.LineColor'),
    },
    {
      key: 'CG_SEARCH_ACTIVE',
      label: translate('metrics.LeadFunnel.Cohort.Stages.CG_SEARCH_ACTIVE'),
      dotColor: translate('client.Status.CG_SEARCH_ACTIVE.MetricsColor.DotColor'),
      lineColor: translate('client.Status.CG_SEARCH_ACTIVE.MetricsColor.LineColor'),
    },
    {
      key: 'CG_PROFILE_SENT',
      label: translate('metrics.LeadFunnel.Cohort.Stages.CG_PROFILE_SENT'),
      dotColor: translate('client.Status.CG_PROFILE_SENT.MetricsColor.DotColor'),
      lineColor: translate('client.Status.CG_PROFILE_SENT.MetricsColor.LineColor'),
    },
    {
      key: 'MEET_GREET',
      label: translate('metrics.LeadFunnel.Cohort.Stages.MEET_GREET'),
      dotColor: translate('client.Status.MEET_GREET.MetricsColor.DotColor'),
      lineColor: translate('client.Status.MEET_GREET.MetricsColor.LineColor'),
    },
    {
      key: 'ACTIVE',
      label: translate('metrics.LeadFunnel.Cohort.Stages.ACTIVE'),
      dotColor: translate('client.Status.ACTIVE.MetricsColor.DotColor'),
      lineColor: translate('client.Status.ACTIVE.MetricsColor.LineColor'),
    },
    {
      key: 'STALLED',
      label: translate('metrics.LeadFunnel.Cohort.Stages.STALLED'),
      dotColor: translate('client.Status.STALLED.MetricsColor.DotColor'),
      lineColor: translate('client.Status.STALLED.MetricsColor.LineColor'),
    },
    {
      key: 'CLOSED_LOST',
      label: translate('metrics.LeadFunnel.Cohort.Stages.CLOSED_LOST'),
      dotColor: translate('client.Status.CLOSED_LOST.MetricsColor.DotColor'),
      lineColor: translate('client.Status.CLOSED_LOST.MetricsColor.LineColor'),
    },
    {
      key: 'NEW_CUSTOMERS',
      label: translate('metrics.LeadFunnel.Cohort.NewCustomers'),
      dotColor: translate('client.Status.ACTIVE.MetricsColor.DotColor'),
      lineColor: translate('client.Status.ACTIVE.MetricsColor.LineColor'),
    },
  ]), []);
  const availableStageList = React.useMemo(() => {
    return stageList.filter((stage) => stageKeys.includes(stage.key));
  }, [stageKeys, stageList]);
  const availableStageKeys = React.useMemo(() => {
    return availableStageList.map((stage) => stage.key);
  }, [availableStageList]);
  const [selectedStageKeys, setSelectedStageKeys] = React.useState(availableStageKeys);

  React.useEffect(() => {
    setSelectedStageKeys(availableStageKeys);
  }, [availableStageKeys]);

  const timezone = 'America/Toronto';
  const currentMetric = currentDate ? metricsByDate[currentDate] || null : null;
  const currentStatusCount = currentMetric?.statusCount || {};
  const comparisonDateText = React.useMemo(() => {
    let formattedCurrentDate = '';
    let formattedPreviousDateRange = '';

    if (currentDate) {
      formattedCurrentDate = moment.tz(currentDate, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY');
    }

    if (previousDates.length) {
      const earliestPreviousDate = previousDates[0];
      const latestPreviousDate = previousDates[previousDates.length - 1];
      const formattedEarliestPreviousDate = moment.tz(earliestPreviousDate, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY');
      const formattedLatestPreviousDate = moment.tz(latestPreviousDate, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY');

      formattedPreviousDateRange = `${formattedEarliestPreviousDate} - ${formattedLatestPreviousDate}`;
    }

    return {
      currentDate: formattedCurrentDate,
      previousDate: formattedPreviousDateRange,
    };
  }, [currentDate, previousDates, timezone]);

  const missingDates = React.useMemo(() => {
    const orderedDates = [...previousDates];

    if (currentDate) {
      orderedDates.push(currentDate);
    }

    const unavailableDates = [];

    orderedDates.forEach((date) => {
      if (metricsByDate[date]) {
        return;
      }

      unavailableDates.push(moment.tz(date, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY'));
    });

    return unavailableDates;
  }, [currentDate, metricsByDate, previousDates, timezone]);

  const {visibleSeries, yAxisMax, xAxisLabels} = React.useMemo(() => {
    const orderedDates = [...previousDates].concat(currentDate).filter(Boolean);
    const selectedStageKeySet = new Set(selectedStageKeys);
    const chartSeriesProps = availableStageList.map((stage) => ({
      id: stage.key,
      label: stage.label,
      color: stage.lineColor,
      data: orderedDates.map((date) => {
        const metric = metricsByDate[date];

        if (!metric) {
          return null;
        }

        return metric.statusCount?.[stage.key]?.length || 0;
      }),
      showMark: false,
      connectNulls: false,
    }));
    const nextVisibleSeries = chartSeriesProps.filter((series) => selectedStageKeySet.has(series.id));

    return {
      visibleSeries: nextVisibleSeries,
      yAxisMax: Math.max(
          1,
          ...nextVisibleSeries.flatMap((series) => series.data.filter((value) => value !== null)),
      ),
      xAxisLabels: orderedDates.map((date) => moment.tz(date, 'YYYY-MM-DD', timezone).format('MMM D')),
    };
  }, [availableStageList, currentDate, metricsByDate, previousDates, selectedStageKeys, timezone]);

  if (isLoading) {
    return (
      <CohortGraphSkeleton
        chartHeight={chartHeight}
        showSummary={showSummary}
      />
    );
  }

  if (errorMessage) {
    return (
      <Alert severity='warning' sx={{mt: 1}}>
        {errorMessage}
      </Alert>
    );
  }

  if (!xAxisLabels.length) {
    return (
      <Alert severity='info' sx={{mt: 1}}>
        <Translate expression='metrics.LeadFunnel.ClientStageMetrics.Unavailable' />
      </Alert>
    );
  }

  return (
    <Box sx={{width: '100%'}}>
      {
        showSummary &&
        <>
          <Typography sx={{fontSize: '13px', fontWeight: 500, color: '#8D84AB', mb: 0.25}}>
            <Translate expression={titleExpression} />
          </Typography>
          <Typography sx={{fontSize: '12px', color: '#9C97A8', mb: 1.5}}>
            <Translate
              expression='metrics.LeadFunnel.ClientStageMetrics.ComparisonDates'
              substitute={comparisonDateText}
            />
          </Typography>
        </>
      }
      {
        missingDates.length > 0 &&
        <Alert severity='info' sx={{mb: 1.5}}>
          <Translate
            expression='metrics.LeadFunnel.ClientStageMetrics.MissingSnapshot'
            substitute={{dates: missingDates.join(', ')}}
          />
        </Alert>
      }
      <Box
        sx={{
          border: '1px solid #EBE6DE',
          borderRadius: 3,
          backgroundColor: '#FFFDFC',
          px: 1,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <LineChart
          height={chartHeight}
          margin={{
            top: 12,
            right: 16,
            bottom: 28,
            left: 36,
          }}
          grid={{horizontal: true}}
          slotProps={{legend: {hidden: true}}}
          xAxis={[
            {
              scaleType: 'point',
              data: xAxisLabels,
            },
          ]}
          yAxis={[
            {
              min: 0,
              max: yAxisMax,
            },
          ]}
          series={visibleSeries}
          sx={{
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
              stroke: '#CEC8BD',
            },
            '& .MuiChartsAxis-tickLabel': {
              fill: '#7B7786',
              fontSize: 11,
            },
            '& .MuiChartsGrid-line': {
              stroke: '#EEE8DD',
              strokeDasharray: '4 4',
            },
          }}
        />
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, px: 0.5, py: 1}}>
          {
            availableStageList.map((stage) => {
              const currentCount = currentMetric ? currentStatusCount[stage.key]?.length || 0 : null;
              const isSelected = selectedStageKeys.includes(stage.key);

              return (
                <Chip
                  key={stage.key}
                  clickable
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (selectedStageKeys.includes(stage.key)) {
                      if (selectedStageKeys.length === 1) {
                        return;
                      }

                      const nextSelectedStageKeys = selectedStageKeys.filter((key) => key !== stage.key);

                      setSelectedStageKeys(nextSelectedStageKeys);
                      return;
                    }

                    setSelectedStageKeys([...selectedStageKeys, stage.key]);
                  }}
                  label={(
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                      <Box
                        sx={{
                          height: 8,
                          width: 8,
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#FFFFFF' : stage.dotColor,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{fontSize: '12px', fontWeight: 600, color: isSelected ? '#FFFFFF' : '#283041'}}>
                        {stage.label}
                      </Typography>
                      <Typography sx={{fontSize: '12px', fontWeight: 700, color: isSelected ? '#FFFFFF' : '#243B63'}}>
                        {currentCount === null ? '--' : currentCount}
                      </Typography>
                    </Box>
                  )}
                  sx={{
                    'height': 'auto',
                    'borderColor': isSelected ? stage.dotColor : '#E6DED2',
                    'backgroundColor': isSelected ? stage.dotColor : '#FFFFFF',
                    'px': 0.5,
                    'py': 0.25,
                    '&:hover': {
                      backgroundColor: isSelected ? stage.dotColor : '#FFFFFF',
                    },
                    '& .MuiChip-label': {
                      display: 'block',
                      px: 1,
                      py: 0.5,
                    },
                  }}
                />
              );
            })
          }
        </Box>
      </Box>
    </Box>
  );
};

CohortGraphData.propTypes = {
  chartHeight: PropTypes.number,
  showSummary: PropTypes.bool,
  titleExpression: PropTypes.string.isRequired,
  stageKeys: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  metricsByDate: PropTypes.object.isRequired,
  currentDate: PropTypes.string.isRequired,
  previousDates: PropTypes.array.isRequired,
};

export default React.memo(CohortGraphData);
